import { createApi } from '@reduxjs/toolkit/query/react';
import {
  getIpawsCombinedAudioFileError,
  getIpawsSpanishAudioBlockError,
} from 'features/Weather/features/WeatherAlerts/features/AlertCreator/utils/ipawsAudio.utils';
import {
  femaStoredAlertRowToThread,
  isFemaStoredAlertRow,
} from 'features/Weather/features/WeatherAlerts/features/FemaAlertList/utils/femaToAlert.adapter';
import { axiosBaseQuery } from 'services/_infra/rtkBaseQuery';
import type {
  FemaAlertContract,
  FemaAlertPayload,
  FemaEnvironmentClass,
  FemaJurisdictions,
  FemaMeta,
  FemaSubmitResponse,
} from 'types/fema.types';
import {
  isFemaMessageRecord,
  type FemaCancelResponse,
  type FemaMessageRecord,
  type FemaUpdateResponse,
} from 'types/femaMessage.types';

export type FemaQueryParams = Record<string, string | number | boolean | undefined>;
export type FemaUnknownRecord = Record<string, unknown>;

export type SubmitFemaAlertRequest = {
  payload: FemaAlertPayload;
  audioFile?: File | null;
  spanishAudioFile?: File | null;
  /**
   * Multi-COG contract §1: the chosen class. It travels as the multipart field `environment`
   * BESIDE `data`, never inside the CAP JSON (`data` stays strict on the backend). Absent = send
   * no class (an older backend, or a workplace that the backend resolves by the §0 rule).
   */
  environment?: FemaEnvironmentClass;
};

/**
 * Builds multipart form data with language-specific audio fields after checking the combined size
 * and the Spanish block.
 * @param request - The alert, optional English and Spanish audio files, and environment.
 * @returns The multipart submission body.
 * @throws {Error} The combined audio size exceeds 4 MiB, or Spanish audio has no es-US block.
 */
export const buildFemaSubmitFormData = ({
  payload,
  audioFile,
  spanishAudioFile,
  environment,
}: SubmitFemaAlertRequest): FormData => {
  const audioSizeError = getIpawsCombinedAudioFileError([audioFile, spanishAudioFile]);
  if (audioSizeError) throw new Error(audioSizeError);
  const spanishBlockError = getIpawsSpanishAudioBlockError(payload.info, spanishAudioFile);
  if (spanishBlockError) throw new Error(spanishBlockError);
  const formData = new FormData();
  formData.append('data', JSON.stringify(payload));
  if (environment) formData.append('environment', environment);
  if (audioFile) formData.append('en-US', audioFile, audioFile.name);
  if (spanishAudioFile) formData.append('es-US', spanishAudioFile, spanishAudioFile.name);
  return formData;
};

/** `GET /fusion/v2/fema/jurisdictions` — the class to read. Absent = no `?environment=`. */
export type GetFemaJurisdictionsRequest = { environment?: FemaEnvironmentClass };

/**
 * Multi-COG contract §1: an Update or a Cancel takes its class from the stored message. The
 * backend refuses an `environment` key on those routes with a 400. This removes the key from any
 * body before it goes out.
 */
export const withoutEnvironmentKey = <T extends object>(body: T): Record<string, unknown> =>
  Object.fromEntries(Object.entries(body).filter(([key]) => key !== 'environment'));

export type FemaApiResponse<T> = {
  result?: T;
  message?: string;
  status?: number;
  error?: string;
};

export type CancelFemaAlertRequest = {
  /** The row id of the message to cancel — the Cancel names that message and no other. */
  messageId: string | number;
  /** Why it is being cancelled. Becomes the CAP `<note>`. */
  reason?: string;
};

export type UpdateFemaAlertRequest = {
  /** The message this Update supersedes, or any member of its chain. */
  messageId: string | number;
  payload: FemaAlertPayload;
};

export const femaApi = createApi({
  reducerPath: 'femaApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['FemaAlert', 'FemaAlertList', 'FemaMeta'],
  endpoints: (builder) => ({
    getFemaAlerts: builder.query<FemaMessageRecord[], FemaQueryParams>({
      query: (params: FemaQueryParams = {}) => ({
        url: '/v2/fema',
        method: 'GET',
        data: undefined,
        params,
        headers: undefined,
        responseType: undefined,
        msAuth: undefined,
      }),
      // The store scopes rows to the caller's workplace on the backend (FE-04 acceptance) — the
      // FE never re-filters by workplaceId; it only validates the shape it got back.
      transformResponse: (response: FemaApiResponse<unknown[]>) => {
        const list = Array.isArray(response?.result)
          ? response.result
          : Array.isArray(response)
            ? response
            : [];
        return list
          .map((record) => {
            if (isFemaMessageRecord(record)) return record;
            if (isFemaStoredAlertRow(record)) {
              return femaStoredAlertRowToThread(record)?.versions[0] ?? null;
            }
            return null;
          })
          .filter((record): record is FemaMessageRecord => record !== null);
      },
      providesTags: ['FemaAlertList'],
    }),
    /**
     * POST /fusion/v2/fema — validated on receipt by the backend. A 400 carries
     * `{ message, valid: false, findings: [{ field, message, stage }] }` (RTK throws it — read
     * `error.data.findings`). A 2xx is a FEMA outcome or, while the send gate is closed, the
     * `REACHED_SEND_POINT` body. There is NO separate validate route.
     */
    submitFemaAlert: builder.mutation<FemaSubmitResponse, SubmitFemaAlertRequest>({
      query: (request: SubmitFemaAlertRequest) => ({
        url: '/v2/fema/',
        method: 'POST',
        data: buildFemaSubmitFormData(request),
        params: undefined,
        // The shared Axios client defaults to JSON. Clearing it lets Axios add the multipart
        // boundary generated for this FormData body.
        headers: { 'Content-Type': undefined },
        responseType: undefined,
        msAuth: undefined,
      }),
      transformResponse: (response: FemaApiResponse<FemaSubmitResponse>) =>
        response?.result ?? (response as FemaSubmitResponse),
      invalidatesTags: ['FemaAlertList'],
    }),
    /**
     * POST /fusion/v2/fema/:id/cancel — ONE CAP Cancel for the ONE message `:id` names (card
     * BE-28). Earlier and later messages in that message's history stay on the air; to put
     * content back afterwards, send an Update. The body is `{ reason? }` and nothing else: a
     * body carrying `scope` is refused with a 400 finding that names the field. 200 means FEMA
     * accepted; 409 carries the same `result` shape with `cancelled: false` and the reason. The
     * backend answers the whole body directly — no generic `{ result }` envelope here, unlike
     * the other FEMA endpoints — so nothing is unwrapped.
     */
    cancelFemaAlert: builder.mutation<FemaCancelResponse, CancelFemaAlertRequest>({
      query: ({ messageId, reason }: CancelFemaAlertRequest) => ({
        url: `/v2/fema/${messageId}/cancel`,
        method: 'POST',
        // No class: the backend takes it from the stored message (multi-COG contract §1).
        data: { ...(reason ? { reason } : {}) },
        params: undefined,
        headers: undefined,
        responseType: undefined,
        msAuth: undefined,
      }),
      transformResponse: (response: FemaCancelResponse) => response,
      invalidatesTags: ['FemaAlertList', 'FemaAlert'],
    }),
    /**
     * POST /fusion/v2/fema/:id/update — a full corrected alert, not a partial edit. `msgType` is
     * forced to `'Update'` and `references` is stripped from the outgoing body: the route builds
     * its own reference from the target's stored bytes and refuses a client-supplied one outright
     * (a caller could otherwise name another COG's message). `accepted` in the answer is the FEMA
     * result — the HTTP status never says so on its own.
     */
    updateFemaAlert: builder.mutation<FemaUpdateResponse, UpdateFemaAlertRequest>({
      query: ({ messageId, payload }: UpdateFemaAlertRequest) => {
        // references is dropped here, never sent: the route builds its own. `environment` is
        // dropped too: the Update takes the class of the stored message (multi-COG contract §1).
        const { references: _references, ...clientPayload } = payload;
        return {
          url: `/v2/fema/${messageId}/update`,
          method: 'POST',
          data: withoutEnvironmentKey({ ...clientPayload, msgType: 'Update' }),
          params: undefined,
          headers: undefined,
          responseType: undefined,
          msAuth: undefined,
        };
      },
      transformResponse: (response: FemaUpdateResponse) => response,
      invalidatesTags: ['FemaAlertList', 'FemaAlert'],
    }),
    /**
     * GET /fusion/v2/fema/meta — the environment, the COG(s) and the service state, relayed
     * from the IpawsService's status (backend GetFemaMeta.controller.ts, 2026-08-28). The FE
     * never decides the environment; the banner reads it here (card FE-01). A 502/503 means
     * the service could not be reached and the banner says so.
     */
    getFemaMeta: builder.query<FemaMeta, void>({
      query: () => ({
        url: '/v2/fema/meta',
        method: 'GET',
        data: undefined,
        params: undefined,
        headers: undefined,
        responseType: undefined,
        msAuth: undefined,
      }),
      transformResponse: (response: FemaApiResponse<FemaMeta>) => response?.result as FemaMeta,
      providesTags: ['FemaMeta'],
    }),
    /**
     * GET /fusion/v2/fema/meta/type — the JSON Schema generated from the same submission model
     * the backend validates. FE-02 uses it to keep controlled CAP values aligned with the wire
     * contract while tenant-specific policy remains in `/meta`.
     */
    getFemaAlertContract: builder.query<FemaAlertContract, void>({
      query: () => ({
        url: '/v2/fema/meta/type',
        method: 'GET',
        data: undefined,
        params: undefined,
        headers: undefined,
        responseType: undefined,
        msAuth: undefined,
      }),
      transformResponse: (response: FemaApiResponse<FemaAlertContract>) =>
        response?.result as FemaAlertContract,
      providesTags: ['FemaMeta'],
    }),
    /**
     * GET /fusion/v2/fema/jurisdictions — the areas the workplace's COG may alert, from the COG
     * Profile FEMA issues (one FEMA query per service process; backend
     * GetFemaJurisdictions.controller.ts, 2026-08-28). The selector and the map read it.
     * Multi-COG contract §1: `{ environment }` sends `?environment=`, and RTK keeps one cache
     * entry per class. The answer echoes `environment`. The callers check the echo with
     * `acceptFemaJurisdictionsForClass` (AlertCreator `utils/femaEnvironment.utils.ts`).
     */
    getFemaJurisdictions: builder.query<FemaJurisdictions, GetFemaJurisdictionsRequest | void>({
      query: (request: GetFemaJurisdictionsRequest | void) => ({
        url: '/v2/fema/jurisdictions',
        method: 'GET',
        data: undefined,
        params: request && request.environment ? { environment: request.environment } : undefined,
        headers: undefined,
        responseType: undefined,
        msAuth: undefined,
      }),
      transformResponse: (response: FemaApiResponse<FemaJurisdictions>) =>
        response?.result as FemaJurisdictions,
      providesTags: ['FemaMeta'],
    }),
  }),
});

export const {
  useSubmitFemaAlertMutation,
  useCancelFemaAlertMutation,
  useUpdateFemaAlertMutation,
  useGetFemaMetaQuery,
  useGetFemaAlertContractQuery,
  useGetFemaJurisdictionsQuery,
  useGetFemaAlertsQuery,
  useLazyGetFemaAlertsQuery,
} = femaApi;
